import React, { useEffect, useState } from "react";
import HostNav from "../components/hostComponents/HostNav";
import { useDispatch, useSelector } from "react-redux";
import {
  getFlags,
  getHostProperties,
  getHostEarnings,
  getAllOrders,
} from "../store/thunks/hostActions";
import { useNavigate, Link } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import {
  ArrowRight,
  Building2,
  DollarSign,
  ShoppingBag,
  TrendingUp,
  Plus,
  Settings,
  Calendar,
  Clock,
  AlertTriangle,
} from "lucide-react";
import {
  updatePropertyReset,
  deletePropertyReset,
  addPropertyReset,
} from "../store/slices/hostSlices";
import request from "../utils/request";
import TutorialModal from "../components/TutorialModal";
import Calendly from "./Calendly";
import { isUpcomingOrder } from "../utils/orderStatus";
import ConciergePromoCard from "../components/hostComponents/ConciergePromoCard";

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();
const isStatus = (status, target) =>
  normalizeStatus(status) === normalizeStatus(target);

/* ─── Compact Analytics Strip ─────────────────────────────────────── */
function AnalyticsBar({
  activeProperties,
  totalEarnings,
  totalOrders,
  avgOrderValue,
}) {
  const navigate = useNavigate();

  const stats = [
    {
      label: "Properties",
      value: String(activeProperties),
      icon: <Building2 size={12} />,
      onClick: () => navigate("/properties"),
    },
    {
      label: "Earnings",
      value: `$${totalEarnings.toFixed(2)}`,
      icon: <DollarSign size={12} />,
      accent: totalEarnings > 0,
      onClick: () => navigate("/earnings"),
    },
    {
      label: "Orders",
      value: String(totalOrders),
      icon: <ShoppingBag size={12} />,
      onClick: () => navigate("/order-list"),
    },
    {
      label: "Avg value",
      value: `$${avgOrderValue.toFixed(2)}`,
      icon: <TrendingUp size={12} />,
      onClick: () => navigate("/earnings"),
    },
  ];

  return (
    <div className="grid grid-cols-2 laptop:grid-cols-4 gap-2.5">
      {stats.map((s) => (
        <button
          key={s.label}
          onClick={s.onClick}
          className="group text-left rounded-[14px] bg-white border border-[#EAEAEA] px-4 py-3.5 hover:border-[#111111] transition-colors"
        >
          <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">
            {s.icon}
            {s.label}
          </div>
          <div
            className={`mt-1 text-[20px] font-extrabold leading-tight tabular-nums ${
              s.accent ? "text-[#111111]" : "text-[#111111]"
            }`}
            style={{ letterSpacing: -0.3 }}
          >
            {s.value}
          </div>
        </button>
      ))}
    </div>
  );
}

/* ─── Upcoming Orders ─────────────────────────────────────────────── */
function UpcomingOrdersWidget({ orders = [], receiveEntryInstructionsEmails = false }) {
  const navigate = useNavigate();
  const safeOrders = Array.isArray(orders) ? orders : [];

  const upcomingOrders = safeOrders
    .filter((order) => isUpcomingOrder(order))
    .sort((a, b) => new Date(a.inStockDate) - new Date(b.inStockDate));

  const ordersNeedingDetails = receiveEntryInstructionsEmails
    ? upcomingOrders.filter((order) => !order.hostOrderDetailsInputted).length
    : 0;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 mb-1">
            <Calendar size={12} className="text-[#EF4949]" />
            <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
              Upcoming
            </span>
          </div>
          <h3
            className="text-[16px] font-extrabold text-[#111111]"
            style={{ letterSpacing: -0.2 }}
          >
            Orders
            <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-[#F7F7F8] text-[10px] font-bold text-[#5F6368] tabular-nums">
              {upcomingOrders.length}
            </span>
          </h3>
        </div>
        <Link
          to="/order-list"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#111111] hover:text-[#EF4949] transition-colors"
        >
          View all
          <ArrowRight size={11} strokeWidth={2.5} />
        </Link>
      </div>

      {receiveEntryInstructionsEmails && ordersNeedingDetails > 0 && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-[10px] bg-[#FEF2F2] border border-[#FECACA]">
          <AlertTriangle size={12} className="text-[#EF4949] flex-shrink-0" />
          <p className="text-[11px] font-semibold text-[#991B1B]">
            <span className="font-extrabold tabular-nums">{ordersNeedingDetails}</span>{" "}
            order{ordersNeedingDetails > 1 ? "s" : ""} need entry instructions
          </p>
        </div>
      )}

      {upcomingOrders.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
          <div className="w-11 h-11 mb-2.5 rounded-[12px] bg-[#F7F7F8] border border-[#EAEAEA] flex items-center justify-center">
            <Clock size={18} className="text-[#888888]" />
          </div>
          <p className="text-[13px] font-bold text-[#111111]">No upcoming orders</p>
          <p className="text-[11px] text-[#888888] mt-0.5">
            New bookings will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-1.5 flex-1 overflow-y-auto min-h-0">
          {upcomingOrders.map((order, index) => {
            const needsDetails =
              receiveEntryInstructionsEmails && !order.hostOrderDetailsInputted;
            return (
              <button
                key={order._id || index}
                onClick={() => navigate(order?._id ? `/order-details/${order._id}` : "/order-list")}
                className={`w-full text-left flex items-center gap-3 p-2.5 rounded-[10px] border transition-colors ${
                  needsDetails
                    ? "bg-[#FEF2F2] border-[#FECACA] hover:bg-[#FEE2E2]"
                    : "bg-[#FAFAFA] border-transparent hover:bg-[#F7F7F8]"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-[8px] flex-shrink-0 flex items-center justify-center ${
                    needsDetails
                      ? "bg-[#EF4949] text-white"
                      : "bg-white border border-[#EAEAEA] text-[#5F6368]"
                  }`}
                >
                  {needsDetails ? <AlertTriangle size={13} /> : <Clock size={13} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-bold text-[#111111] truncate tabular-nums">
                      #
                      {order.uniqueId ||
                        order._id?.slice(-8) ||
                        `ORD-${index + 1}`}
                    </span>
                    {needsDetails && (
                      <span className="px-1.5 py-0.5 rounded-[4px] bg-[#EF4949] text-white text-[8px] font-bold uppercase tracking-[0.1em]">
                        Needs info
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#888888] truncate">
                    {order.property?.listingTitle ||
                      order.property?.address?.city ||
                      "Property"}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p
                    className={`text-[12px] font-extrabold tabular-nums ${
                      needsDetails ? "text-[#EF4949]" : "text-[#111111]"
                    }`}
                  >
                    ${order.hostFee?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-[9px] text-[#888888] mt-0.5">
                    {formatDate(order.inStockDate || order.createdAt)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Quick Actions ───────────────────────────────────────────────── */
function QuickActionsWidget() {
  const navigate = useNavigate();
  const { hostInfo } = useSelector((state) => state.host);

  const actions = [
    {
      title: "Add property",
      description: "List a new rental",
      icon: <Plus size={14} strokeWidth={2.5} />,
      link: "/add-property",
      accent: true,
    },
    {
      title: "View orders",
      description: "Check recent bookings",
      icon: <ShoppingBag size={14} />,
      link: "/order-list",
    },
    {
      title: "Earnings",
      description: "Track performance",
      icon: <DollarSign size={14} />,
      link: "/earnings",
    },
    {
      title: "Settings",
      description: "Account & profile",
      icon: <Settings size={14} />,
      link: `/settings/${hostInfo?._id}`,
    },
  ];

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] p-5 h-full flex flex-col">
      <div className="mb-4">
        <div className="inline-flex items-center gap-1.5 mb-1">
          <TrendingUp size={12} className="text-[#EF4949]" />
          <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
            Actions
          </span>
        </div>
        <h3
          className="text-[16px] font-extrabold text-[#111111]"
          style={{ letterSpacing: -0.2 }}
        >
          Quick actions
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-2 flex-1">
        {actions.map((action) => (
          <button
            key={action.title}
            onClick={() => navigate(action.link)}
            className="group text-left p-3 rounded-[12px] bg-[#FAFAFA] border border-[#EAEAEA] hover:bg-white hover:border-[#111111] transition-all"
          >
            <div
              className={`w-8 h-8 rounded-[8px] flex items-center justify-center mb-2 transition-colors ${
                action.accent
                  ? "bg-[#EF4949] text-white"
                  : "bg-white border border-[#EAEAEA] text-[#5F6368] group-hover:bg-[#111111] group-hover:text-white group-hover:border-[#111111]"
              }`}
            >
              {action.icon}
            </div>
            <div
              className="text-[12px] font-bold text-[#111111]"
              style={{ letterSpacing: -0.1 }}
            >
              {action.title}
            </div>
            <div className="text-[10px] text-[#888888] mt-0.5 leading-snug">
              {action.description}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */
const HostScreen = () => {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [gateLoading, setGateLoading] = useState(true);
  const [onboardingGate, setOnboardingGate] = useState({
    shouldShowTutorial: false,
    shouldShowOnboardingCall: false,
    isOnBoarded: false,
    activePropertyCount: 0,
    hasCompletedTutorial: false,
    tutorialCompletedAt: null,
  });
  const [allOrders, setAllOrders] = useState([]);

  const { hostInfo } = useSelector((state) => state.host);
  const hostDetails = useSelector((state) => state.hostDetails.Info);
  const isOnBoarded = hostDetails?.isOnBoarded;

  const [, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!hostInfo?._id) return;

    const fetchData = async () => {
      try {
        const response = await request.get(
          `properties/host/${hostInfo._id}/commission`
        );
        setData(response.data.commission);
      } catch (error) {
        console.error("An error occurred:", error);
        setData([]);
      }
    };

    const fetchOrders = async () => {
      try {
        const action = await dispatch(getAllOrders(hostInfo._id));
        const fetchedOrders = action?.payload?.orders;
        setAllOrders(Array.isArray(fetchedOrders) ? fetchedOrders : []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setAllOrders([]);
      }
    };

    if (hostInfo._id) {
      setIsLoading(true);
      dispatch(getFlags(hostInfo._id));
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      fetchData();
      fetchOrders();
      setIsLoading(false);
    }
  }, [hostInfo?._id, dispatch]);

  useEffect(() => {
    if (!hostInfo?._id) return;

    const fetchOnboardingGate = async () => {
      setGateLoading(true);
      try {
        const { data } = await request.get("/hosts/onboarding-gate", {
          withCredentials: true,
        });
        const gate = data?.gate || {};
        setOnboardingGate({
          shouldShowTutorial: Boolean(gate.shouldShowTutorial),
          shouldShowOnboardingCall: Boolean(gate.shouldShowOnboardingCall),
          isOnBoarded: Boolean(gate.isOnBoarded),
          activePropertyCount: Number(gate.activePropertyCount || 0),
          hasCompletedTutorial: Boolean(gate.hasCompletedTutorial),
          tutorialCompletedAt: gate.tutorialCompletedAt || null,
        });
        setShowTutorial(Boolean(gate.shouldShowTutorial));
      } catch (error) {
        console.error("Error fetching onboarding gate:", error);
        setOnboardingGate({
          shouldShowTutorial: false,
          shouldShowOnboardingCall: false,
          isOnBoarded: false,
          activePropertyCount: 0,
          hasCompletedTutorial: false,
          tutorialCompletedAt: null,
        });
        setShowTutorial(false);
      } finally {
        setGateLoading(false);
      }
    };

    fetchOnboardingGate();
  }, [hostInfo?._id]);

  const { loading: loadingProperties, properties, error } = useSelector(
    (state) => state.hostProperties
  );

  const { receiveEntryInstructionsEmails } = useSelector(
    (state) => state.hostFlags
  );

  const { success: successUpdate, message: messsageupdate } = useSelector(
    (state) => state.propertyUpdate
  );
  const { success: successDelete, message: messageDelete } = useSelector(
    (state) => state.propertyDelete
  );
  const { success: successAddProperty, msg: messageAddProperty } = useSelector(
    (state) => state.addProperty
  );

  useEffect(() => {
    if ((successUpdate || successDelete) && hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
    }
  }, [successUpdate, successDelete, dispatch, hostInfo?._id]);

  useEffect(() => {
    if (successUpdate || successDelete || successAddProperty) {
      setTimeout(() => {
        successUpdate && dispatch(updatePropertyReset());
        successDelete && dispatch(deletePropertyReset());
        successAddProperty && dispatch(addPropertyReset());
      }, 2000);
    }
  }, [successAddProperty, successUpdate, successDelete, dispatch]);

  const handleNext = () => setTutorialStep((prev) => prev + 1);
  const handleBack = () => setTutorialStep((prev) => prev - 1);
  const handleClose = async () => {
    try {
      await request.post(
        "/hosts/onboarding-gate/complete",
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error completing tutorial gate:", error);
    }
    setShowTutorial(false);
    setOnboardingGate((prev) => ({
      ...prev,
      shouldShowTutorial: false,
      shouldShowOnboardingCall: false,
      hasCompletedTutorial: true,
      tutorialCompletedAt: new Date().toISOString(),
    }));
  };

  const activeProperties = Array.isArray(properties)
    ? properties.filter((p) => !p.isDeleted)
    : [];
  const uniqueOrders = Array.from(
    new Map(
      (Array.isArray(allOrders) ? allOrders : []).map((order) => [
        order?._id || order?.uniqueId,
        order,
      ])
    ).values()
  );
  const finalizedOrders = uniqueOrders.filter(
    (order) => !isStatus(order?.status, "created")
  );
  const earnedOrders = finalizedOrders.filter(
    (order) =>
      isStatus(order?.status, "completed") ||
      isStatus(order?.status, "paymentCompleted")
  );
  const totalOrders = finalizedOrders.length;
  const totalEarnings = earnedOrders.reduce(
    (sum, order) => sum + (Number(order?.hostFee) || 0),
    0
  );
  const avgOrderValue =
    earnedOrders.length > 0 ? totalEarnings / earnedOrders.length : 0;

  if (loadingProperties || isLoading || gateLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={44} sx={{ color: "#EF4949" }} />
          <p className="mt-4 text-[12px] font-bold uppercase tracking-[0.22em] text-[#888888]">
            Loading dashboard
          </p>
        </div>
      </div>
    );
  }

  /* Onboarding call state */
  if (onboardingGate.shouldShowOnboardingCall) {
    return (
      <div
        className="min-h-screen bg-[#FAFAFA]"
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {showTutorial && (
          <TutorialModal
            step={tutorialStep}
            onNext={handleNext}
            onBack={handleBack}
            onClose={handleClose}
          />
        )}
        <div className="max-w-3xl mx-auto px-6 py-10">
          <HostNav searchBar={false} setSearch={setSearch} />
          <div className="mt-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-[14px] bg-[#111111]">
              <Calendar size={22} className="text-white" />
            </div>
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-2">
              Final step
            </p>
            <h2
              className="text-[28px] font-extrabold text-[#111111]"
              style={{ letterSpacing: -0.5 }}
            >
              Book your onboarding call.
            </h2>
            <p className="mt-2 text-[13px] text-[#5F6368] max-w-md mx-auto">
              Pick a time that works for you. We'll walk you through setup,
              answer questions, and get you earning.
            </p>
          </div>
          <div className="mt-6 rounded-[20px] border border-[#EAEAEA] bg-white p-6 shadow-[0_18px_60px_rgba(0,0,0,0.04)]">
            <Calendly />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#FAFAFA]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div className="max-w-[1280px] mx-auto px-6 py-6">
        <HostNav searchBar={true} setSearch={setSearch} />

        {/* Flash messages */}
        <div className="space-y-2 mb-3">
          {successAddProperty && (
            <Alert severity="success" sx={{ borderRadius: "12px" }}>
              {messageAddProperty}
            </Alert>
          )}
          {successUpdate && (
            <Alert severity="success" sx={{ borderRadius: "12px" }}>
              {messsageupdate}
            </Alert>
          )}
          {successDelete && (
            <Alert severity="warning" sx={{ borderRadius: "12px" }}>
              {messageDelete}
            </Alert>
          )}
        </div>

        {isOnBoarded === false ? (
          <div className="rounded-[20px] bg-white border border-[#EAEAEA] p-8 shadow-[0_18px_60px_rgba(0,0,0,0.04)]">
            <div className="text-center max-w-md mx-auto">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-[14px] bg-[#111111]">
                <TrendingUp size={22} className="text-white" />
              </div>
              <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-2">
                Get started
              </p>
              <h3
                className="text-[22px] font-extrabold text-[#111111]"
                style={{ letterSpacing: -0.4 }}
              >
                Complete your Bellhopt setup.
              </h3>
              <p className="mt-2 text-[13px] text-[#5F6368]">
                Finish property setup, access details, and activation at your
                own pace. Stripe can be connected now or later for payouts.
              </p>
              <Link
                to="/onboarding"
                className="mt-5 inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-[12px] bg-[#111111] text-white text-[14px] font-bold hover:bg-black transition-colors"
              >
                Continue setup
                <ArrowRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {error && (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {error}
              </Alert>
            )}

            <ConciergePromoCard hostId={hostInfo._id} />

            <AnalyticsBar
              activeProperties={activeProperties.length}
              totalEarnings={totalEarnings}
              totalOrders={totalOrders}
              avgOrderValue={avgOrderValue}
            />

            <div className="grid grid-cols-1 laptop:grid-cols-2 gap-5 min-h-[380px]">
              <UpcomingOrdersWidget
                orders={allOrders}
                receiveEntryInstructionsEmails={receiveEntryInstructionsEmails}
              />
              <QuickActionsWidget />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostScreen;
